import { CalendarDays, FileText } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { api, type PublicSubmissionEntry } from "../api.js";
import { apiErrorMessage } from "../../api/client.js";
import { BrandMark } from "../../design/components/BrandMark.js";
import { Button } from "../../design/components/Button.js";
import { Alert, Skeleton } from "../../design/components/Feedback.js";

function dateLabel(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(value));
}

export function PublicSubmissionEntryPage() {
  const { organizationId = "", publicSlug = "" } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<PublicSubmissionEntry | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const idempotencyKey = useRef(crypto.randomUUID());

  useEffect(() => {
    let active = true;
    setError("");
    void api.publicSubmissionEntry(organizationId, publicSlug)
      .then((result) => { if (active) setEntry(result); })
      .catch((reason) => {
        if (active) setError(apiErrorMessage(reason, "This submission window is unavailable."));
      });
    return () => { active = false; };
  }, [organizationId, publicSlug]);

  const start = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await api.startPublicSubmission(
        organizationId,
        publicSlug,
        idempotencyKey.current,
      );
      navigate(result.destination, { replace: true });
    } catch (reason) {
      setError(apiErrorMessage(reason, "Your submission could not be started. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return <main className="public-submission-page">
    <section className="public-submission-card">
      <header className="public-submission-brand"><BrandMark /><span>Author submission</span></header>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {!entry && !error ? <div className="public-submission-loading"><Skeleton /><Skeleton /></div> : null}
      {entry ? <>
        <div className="public-submission-heading">
          <p>{entry.conference.short_name || "Conference submissions"}</p>
          <h1>Submit to {entry.conference.name}</h1>
          <span>{entry.phase.instructions}</span>
        </div>
        <dl className="public-submission-details">
          <div><dt><FileText aria-hidden="true" size={18} />Submission phase</dt><dd>{entry.phase.name}</dd></div>
          <div><dt><CalendarDays aria-hidden="true" size={18} />Deadline</dt><dd>{dateLabel(entry.phase.closes_at)}</dd></div>
        </dl>
        <div className="public-submission-actions">
          {entry.availability === "OPEN" ? <Button type="button" busy={busy} onClick={() => void start()}>Start a submission</Button> : null}
          {entry.availability === "SCHEDULED" ? <Alert tone="neutral">Submissions open {dateLabel(entry.phase.opens_at)}. Your account is ready; return through this link when the phase opens.</Alert> : null}
          {entry.availability === "CLOSED" ? <Alert tone="danger">This submission phase closed {dateLabel(entry.phase.closes_at)}.</Alert> : null}
          <p>Your draft saves automatically. Nothing is submitted until you confirm the completed form.</p>
        </div>
      </> : null}
    </section>
  </main>;
}
